import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { Conversation, BrokerLite } from '../components/conversations/types';

const PAGE_SIZE = 40;
/** CONV2 (c): how often the list refreshes on its own while the page is visible. */
export const LIST_REFRESH_MS = 30_000;

// Owns the conversation list: filters, debounced search, keyset pagination, refresh (his button, and on its own).
export function useConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [channel, setChannel] = useState('');
  const [unread, setUnread] = useState(false);
  const [owner, setOwner] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [brokers, setBrokers] = useState<Record<string, BrokerLite>>({});
  // Each load is numbered: a background refresh lands only if no load (a new filter, a new search) started after it.
  const listSeq = useRef(0);
  const backgroundBusy = useRef(false);
  // How many rows are on screen - a background refresh that lands on an EMPTY list (its first load failed) also takes
  // the next-page cursor, or the first 40 of thousands would read "End of conversations".
  const rowCount = useRef(0);
  useEffect(() => { rowCount.current = conversations.length; }, [conversations]);

  // Debounce the search box before hitting the server.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const buildParams = useCallback(
    (cursor?: string | null) => {
      const p: Record<string, string> = { limit: String(PAGE_SIZE) };
      if (channel) p.channel = channel;
      if (unread) p.unread = 'true';
      if (owner) p.owner = owner;
      if (debouncedQuery) p.query = debouncedQuery;
      if (cursor) p.cursor = cursor;
      return p;
    },
    [channel, unread, owner, debouncedQuery]
  );

  const load = useCallback(async () => {
    const seq = ++listSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.listConversations(buildParams());
      if (seq !== listSeq.current) return;
      setConversations(res.conversations || []);
      setTotal(res.total || 0);
      setNextCursor(res.nextCursor || null);
      if (res.brokers) setBrokers(res.brokers);
    } catch (e: any) {
      if (seq !== listSeq.current) return;
      setError(e?.message || 'Failed to load conversations');
    } finally {
      if (seq === listSeq.current) setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    load();
  }, [load]);

  // CONV2 (c), the 142nd: "new messages appear without my pressing refresh, while the page is open and when I come
  // back to it." Before this only his Refresh button updated the app's copy of the conversations. Now: ask the server
  // for an AUTOMATIC sync (it runs only if its copy is stale - one per 20 s for every open tab), then read the first
  // page and merge it in: a conversation with a new message moves to the top, the rows he paged in stay, and nothing
  // flashes "Loading". A failure is quiet - the list stays and the next refresh tries again.
  const backgroundRefresh = useCallback(async () => {
    if (backgroundBusy.current || document.visibilityState !== 'visible') return;
    backgroundBusy.current = true;
    const seq = listSeq.current;
    try {
      // A failed sync still reads the list: another tab or his Refresh button may have moved the copy since.
      await api.syncConversations('incremental', { auto: true }).catch(() => undefined);
      const res = await api.listConversations(buildParams());
      if (seq !== listSeq.current) return;
      const first: Conversation[] = res.conversations || [];
      const fresh = new Set(first.map((c) => c.id));
      if (rowCount.current === 0) setNextCursor(res.nextCursor || null);
      setConversations((prev) => [...first, ...prev.filter((c) => !fresh.has(c.id))]);
      setTotal(res.total || 0);
      if (res.brokers) setBrokers(res.brokers);
      // I2 (the review): the list draws `error ? message : rows`, so a message left standing hid every row this read
      // just brought back - after a failed first load or a failed Refresh, however many refreshes succeeded after it.
      setError(null);
    } catch {
      /* quiet - his Refresh button is the one that reports */
    } finally {
      backgroundBusy.current = false;
    }
  }, [buildParams]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') void backgroundRefresh();
    };
    const timer = window.setInterval(refreshIfVisible, LIST_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshIfVisible);
    window.addEventListener('focus', refreshIfVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.removeEventListener('focus', refreshIfVisible);
    };
  }, [backgroundRefresh]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.listConversations(buildParams(nextCursor));
      setConversations((prev) => [...prev, ...(res.conversations || [])]);
      setNextCursor(res.nextCursor || null);
    } catch {
      /* keep what we have on a paging error */
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, buildParams]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await api.syncConversations('incremental');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  return {
    conversations,
    total,
    loading,
    loadingMore,
    error,
    channel,
    setChannel,
    unread,
    setUnread,
    owner,
    setOwner,
    brokers,
    query,
    setQuery,
    hasMore: !!nextCursor,
    loadMore,
    refresh,
    refreshing,
    reload: load,
  };
}
